import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiHelpCircle, FiInfo, FiSend, FiBookOpen, FiMessageCircle } from 'react-icons/fi';
import * as chatwoot from '../../services/chatwoot';
import { TzamunAiMark, TzamunAiLogo } from '../TzamunAiBrand';
import { Button, Input, Drawer } from '../ui';
import { AnswerBody, FeedbackButtons, SectionMedia } from '../../pages/UserManual';

// Route → manual section id (longest prefix wins). Pages without a natural
// section fall back to the dashboard chapter + the full-manual link.
//
// HR statutory v2, T12: /hr/employees/:id (the employee FILE — contract,
// documents, terminate, EOSB settlement) used to fall through the plain
// '/hr/employees' rule below and show the "add employee" wizard content
// instead. '/hr/employees/new' is carved out first (still the wizard), then
// the trailing-slash rule below catches every '/hr/employees/<id>' path and
// routes it to 'hr-employee-file'; the bare list page keeps matching the
// original '/hr/employees' rule since it has no trailing slash to match.
const PAGE_SECTIONS = [
  ['/hr/reports/profitability', 'hr-profitability'],
  ['/hr/settings', 'hr-payroll'],
  ['/hr/reports', 'hr-reports'],
  ['/hr/payroll', 'hr-payroll'],
  ['/hr/employees/new', 'hr-employees'],
  ['/hr/employees/', 'hr-employee-file'],
  ['/hr/employees', 'hr-employees'],
  ['/hr', 'hr-employees'],
  ['/inventory-locations', 'inventory-locations'],
  ['/requests', 'staff-requests'],
  ['/feedback', 'customer-feedback'],
  ['/settings/data-export', 'data-export'],
  ['/settings/delay-fees', 'delay-fees'],
  ['/assistant', 'ai-assistant'],
  ['/job-cards', 'job-card-create'],
  ['/jobs', 'tasks'],
  ['/task-monitoring', 'task-monitoring'],
  ['/invoices', 'invoices'],
  ['/quotations', 'quotations'],
  ['/customers', 'customers'],
  ['/vehicles', 'vehicles'],
  ['/appointments', 'appointments'],
  ['/whatsapp/marketing', 'whatsapp-marketing'],
  ['/whatsapp', 'whatsapp'],
  ['/parts', 'inventory'],
  ['/categories', 'part-units'],
  ['/purchases', 'purchases-expenses'],
  ['/financials/expenses', 'purchases-expenses'],
  ['/financials/journal-entries', 'journal-entries'],
  ['/financials/trial-balance', 'trial-balance'],
  ['/financials/period-close', 'period-close'],
  ['/financials/chart-of-accounts', 'chart-of-accounts'],
  ['/financials/opening-balances', 'opening-balances'],
  ['/financials/fixed-assets', 'fixed-assets'],
  ['/financials/position', 'cash-reports'],
  ['/financials/cash', 'cash-reports'],
  ['/financials/audit', 'cash-reports'],
  ['/payments', 'payments'],
  ['/payouts', 'cash-reports'],
  ['/reports', 'cash-reports'],
  ['/services', 'services-parts'],
  ['/users', 'users-roles'],
  ['/settings', 'settings'],
  ['/', 'dashboard'],
];

let manualCache = null; // module-level: fetched once per app session

export default function HelpButton() {
  const { t, i18n } = useTranslation();
  const L = (i18n.language || 'en').startsWith('ar') ? 'ar' : 'en';
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  // Subscribed, not polled. The SDK finishing its handshake is not a React
  // state change, so reading isReady() during render left the support entry
  // missing on any drawer opened before the widget booted.
  //
  // Two flags, not one: `configured` decides whether the entry EXISTS, `ready`
  // only whether it can open right now. Hiding it on !ready made a Chatwoot
  // outage look like the feature had been removed.
  const [support, setSupport] = useState(() => ({
    configured: chatwoot.isConfigured(),
    ready: chatwoot.isReady(),
  }));
  const [manual, setManual] = useState(manualCache);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState(null);

  const sectionId = useMemo(() => {
    const path = location.pathname;
    const hit = PAGE_SECTIONS.find(([prefix]) => (prefix === '/' ? path === '/' : path.startsWith(prefix)));
    return hit ? hit[1] : 'dashboard';
  }, [location.pathname]);

  useEffect(() => {
    if (open && !manualCache) {
      axios.get('/api/manual').then(({ data }) => {
        manualCache = data.data;
        setManual(manualCache);
      }).catch(() => {});
    }
  }, [open]);

  // The widget boots asynchronously and may finish after this mounts.
  useEffect(() => chatwoot.onStateChange(() => setSupport({
    configured: chatwoot.isConfigured(),
    ready: chatwoot.isReady(),
  })), []);

  // Answers are page-scoped; drop them when navigating elsewhere.
  useEffect(() => {
    setAnswer(null);
    setQuestion('');
  }, [sectionId]);

  const section = manual?.find((s) => s.id === sectionId);

  const ask = async (text) => {
    const q = (typeof text === 'string' ? text : question).trim();
    if (!q) return;
    setQuestion(q);
    setAsking(true);
    setAnswer(null);
    try {
      const { data } = await axios.post('/api/manual/ask', { question: q, page: sectionId });
      setAnswer(data.data);
    } catch (e) {
      toast.error(
        e.response?.data?.code === 'ai_quota_exceeded' ? t('manual.quotaError') : t('manual.askFailed')
      );
    } finally {
      setAsking(false);
    }
  };

  // The manual page IS the help — no button there.
  if (location.pathname.startsWith('/manual')) return null;

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        aria-label={t('manual.helpTitle')}
      >
        <FiHelpCircle size={18} />
      </Button>

      <Drawer open={open} onClose={() => setOpen(false)} title={t('manual.helpTitle')} size="md">
        <div className="space-y-4 p-1">
          {section && (
            <>
              <h3 className="font-bold text-content">{section.title[L]}</h3>
              <p className="text-sm text-content-secondary leading-7">{section.intro[L]}</p>
              <ol className="space-y-2">
                {section.steps.map((st, i) => (
                  <li key={i} className="flex gap-3 text-sm text-content leading-7">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-accent/10 text-accent text-xs font-bold flex items-center justify-center mt-1">
                      {i + 1}
                    </span>
                    <span>{st[L]}</span>
                  </li>
                ))}
              </ol>
              {section.tips.map((tip, i) => (
                <div
                  key={i}
                  className="flex gap-2 text-xs rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-200 p-3 leading-6"
                >
                  <FiInfo className="shrink-0 mt-0.5" />
                  <span>{tip[L]}</span>
                </div>
              ))}
              <SectionMedia media={section.media} lang={L} />
            </>
          )}

          <div className="border-t border-edge pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <TzamunAiMark variant="color" className="h-5" />
                <span className="font-bold text-sm text-content">{t('manual.askTitle')}</span>
              </div>
              <div className="flex items-center gap-1 text-[10px] text-content-muted">
                <span>{t('manual.poweredBy')}</span>
                <TzamunAiLogo className="h-3" />
              </div>
            </div>
            <div className="flex gap-2">
              <Input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !asking && ask()}
                placeholder={t('manual.helpAskPlaceholder')}
                maxLength={500}
                className="flex-1"
              />
              <Button onClick={() => ask()} loading={asking} disabled={!question.trim()} size="icon" aria-label={t('manual.askButton')}>
                <FiSend size={16} />
              </Button>
            </div>
            {section?.suggested?.length > 0 && !answer && !asking && (
              <div className="mt-2 flex flex-wrap gap-2">
                {section.suggested.slice(0, 3).map((sq) => (
                  <button
                    key={sq.en}
                    onClick={() => ask(sq[L])}
                    className="px-3 py-1.5 rounded-full text-xs bg-accent/5 border border-accent/20 text-accent hover:bg-accent/15"
                  >
                    {sq[L]}
                  </button>
                ))}
              </div>
            )}
            {answer && (
              <div className="mt-3 rounded-xl bg-surface-secondary p-4 border border-edge">
                <AnswerBody text={answer.answer} />
                <FeedbackButtons questionId={answer.question_id} />
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <button
              onClick={() => {
                setOpen(false);
                navigate('/manual');
              }}
              className="flex items-center gap-2 text-sm text-accent hover:underline"
            >
              <FiBookOpen size={16} /> {t('manual.openManual')}
            </button>

            {/* Present whenever the server configured support — including while
                the widget is still booting or has failed to. A button that says
                why it cannot open beats one that quietly disappears. */}
            {support.configured && (
              <button
                onClick={() => {
                  if (!chatwoot.open()) {
                    toast.error(t('manual.supportUnavailable'));
                    return;
                  }
                  setOpen(false);
                }}
                className="flex items-center gap-2 text-sm text-accent hover:underline"
                data-testid="help-contact-support"
              >
                <FiMessageCircle size={16} /> {t('manual.contactSupport')}
              </button>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
}
